import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { App, Alert, Button, Card, Col, DatePicker, Empty, Input, Modal, Progress, Row, Space, Statistic, Table, Tabs, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, PlayCircleOutlined, BarChartOutlined, SettingOutlined, AuditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, { BLOCKED_LINE_ACTIONS, type WorkspaceData } from '@/services/financeRecognition.service';
import { StatusTag, amount, apiErrorMessage, contractPath, humanize, jvPath, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

type BlockedLine = WorkspaceData['blocked_lines'][number];

const PrepaidWorkspacePage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'due';
  const [asOf, setAsOf] = useState<dayjs.Dayjs>(dayjs());
  const [selectedDue, setSelectedDue] = useState<string[]>([]);
  const [blockedAction, setBlockedAction] = useState<{ line: BlockedLine; action: string } | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [discardTarget, setDiscardTarget] = useState<string | null>(null);

  const asOfKey = asOf.format('YYYY-MM-DD');
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['recognition', 'workspace', asOfKey],
    queryFn: () => recognitionService.workspace(asOfKey),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recognition'] });

  const postLines = useMutation({
    mutationFn: (ids: string[]) => recognitionService.manualPost(ids),
    onSuccess: (outcomes) => {
      const posted = outcomes.filter((o) => o.status === 'POSTED').length;
      const drafts = outcomes.filter((o) => o.status === 'DRAFT_CREATED').length;
      const failed = outcomes.filter((o) => o.status === 'EXCEPTION');
      if (failed.length) message.warning(`${posted} posted, ${drafts} drafted, ${failed.length} failed: ${failed[0].message ?? failed[0].exceptionCode}`);
      else message.success(`${posted} posted${drafts ? `, ${drafts} draft journal(s) created for approval` : ''}`);
      setSelectedDue([]);
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Posting failed')),
  });

  const postDraft = useMutation({
    mutationFn: (postingId: string) => recognitionService.postDraft(postingId),
    onSuccess: (r) => {
      message.success(`Journal ${r.voucherNumber} posted`);
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Posting failed')),
  });

  const discardDraft = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => recognitionService.discardDraft(id, reason),
    onSuccess: () => {
      message.success(t('draftJournalDiscarded'));
      setDiscardTarget(null);
      setActionReason('');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Discard failed')),
  });

  const blockedMutation = useMutation({
    mutationFn: ({ lineId, action, reason }: { lineId: string; action: string; reason: string }) =>
      recognitionService.blockedLineAction(lineId, action, reason, action === 'POST_TO_NEXT_OPEN_PERIOD' || action === 'CURRENT_PERIOD_ADJUSTMENT'),
    onSuccess: (r) => {
      message.success(r.message || 'Line updated');
      setBlockedAction(null);
      setActionReason('');
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Action failed')),
  });

  const k = data?.kpis;
  const totalRemaining = Number(k?.remaining_base ?? 0);

  const kpis = useMemo(
    () =>
      k
        ? [
            { title: t('totalPrepaidAssets'), value: amount(k.remaining_base), sub: t('liveContractsCount', { count: k.live_contracts }), to: '/fin/prepaid/contracts?status=SCHEDULED,ACTIVE,SUSPENDED,TERMINATING' },
            { title: t('recognitionDueToday'), value: amount(k.due_today_base), sub: t('linesCount', { count: k.due_today_count }), tab: 'due' },
            { title: t('dueThisMonth'), value: amount(k.remaining_this_month), sub: t('recognisedSoFar', { amount: amount(k.recognized_this_month) }), tab: 'due' },
            { title: t('overdue'), value: amount(k.overdue_base), sub: t('linesBeforeThisMonth', { count: k.overdue_count }), tab: 'due', danger: k.overdue_count > 0 },
            { title: t('pendingApproval'), value: String(k.contracts_pending_approval + k.adjustments_pending_approval), sub: t('contractsAndAdjustments', { contracts: k.contracts_pending_approval, adjustments: k.adjustments_pending_approval }), tab: 'approvals' },
            { title: t('failingRecognition'), value: String(k.blocked_count + k.adjustments_failed), sub: t('blockedAndFailedRuns', { blocked: k.blocked_count, failed: k.failed_runs_30_days }), tab: 'blocked', danger: k.blocked_count + k.adjustments_failed > 0 },
            { title: t('expiringWithin30Days'), value: String(k.expiring_30_days), sub: amount(k.expiring_30_days_base), tab: 'expiring' },
            { title: t('suspendedContracts'), value: String(k.suspended_contracts), sub: t('recognitionPaused'), to: '/fin/prepaid/contracts?status=SUSPENDED' },
            { title: t('fullyRecognised'), value: String(k.fully_recognized_contracts), sub: t('readyToClose'), to: '/fin/prepaid/contracts?status=FULLY_RECOGNIZED' },
            { title: t('draftJournals'), value: String(k.draft_count), sub: amount(k.draft_base), tab: 'drafts' },
          ]
        : [],
    [k, t]
  );

  if (!access.canView) {
    return <Alert type="warning" showIcon message={t('noAccessPrepaidWorkspace')} style={{ margin: 24 }} />;
  }

  const forecastPeriods = (data?.forecast.periods ?? []).filter((p) => p !== 'OVERDUE' && p !== 'LATER');
  const forecastMax = Math.max(1, ...forecastPeriods.map((p) => Number(data?.forecast.totals[p] ?? 0)));
  const aging = data?.expiry_aging;
  const agingRows = aging
    ? [
        { bucket: t('agingPastEndDate'), value: aging.past_end },
        { bucket: t('aging0To30'), value: aging.d0_30 },
        { bucket: t('aging31To90'), value: aging.d31_90 },
        { bucket: t('aging91To180'), value: aging.d91_180 },
        { bucket: t('aging181To365'), value: aging.d181_365 },
        { bucket: t('agingOver1Year'), value: aging.over_365 },
      ]
    : [];

  const shareBar = (value: string) => (
    <Progress percent={totalRemaining > 0 ? Math.round((Number(value) / totalRemaining) * 100) : 0} size="small" showInfo={false} />
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('prepaidExpenseWorkspace')}
          </Title>
          <Text type="secondary">{data ? t('amountsInBaseAsOf', { date: data.as_of }) : t('amountsInBaseCurrency')}</Text>
        </div>
        <Space wrap>
          <DatePicker value={asOf} allowClear={false} onChange={(d) => d && setAsOf(d)} />
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            {tc('refresh')}
          </Button>
          <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/fin/prepaid/contracts')}>
            {t('contracts')}
          </Button>
          {access.canRun && (
            <Button icon={<PlayCircleOutlined />} onClick={() => navigate('/fin/prepaid/runs')}>
              {t('recognitionRuns')}
            </Button>
          )}
          <Button icon={<BarChartOutlined />} onClick={() => navigate('/fin/prepaid/reports/register')}>
            {t('reports')}
          </Button>
          {access.canReconcile && (
            <Button icon={<AuditOutlined />} onClick={() => navigate('/fin/prepaid/reconciliation')}>
              {t('reconciliation')}
            </Button>
          )}
          {access.canAdmin && (
            <Button icon={<SettingOutlined />} onClick={() => navigate('/fin/prepaid/settings')}>
              {t('settings')}
            </Button>
          )}
          {access.canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/prepaid/new')}>
              {t('newPrepaid')}
            </Button>
          )}
        </Space>
      </Space>

      {isError && <Alert type="error" showIcon message={apiErrorMessage(error, 'Could not load the workspace')} style={{ marginBottom: 16 }} />}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {kpis.map((kpi) => (
          <Col key={kpi.title} xs={24} sm={12} md={8} lg={6} xl={4} xxl={4}>
            <Card
              size="small"
              hoverable
              loading={isLoading}
              onClick={() => (kpi.to ? navigate(kpi.to) : setParams({ tab: kpi.tab ?? 'due' }))}
              style={kpi.danger ? { borderColor: '#ff7875' } : undefined}
            >
              <Statistic title={kpi.title} value={kpi.value} valueStyle={{ fontSize: 20, color: kpi.danger ? '#cf1322' : undefined }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {kpi.sub}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title={t('recognitionForecastNext12')} extra={<Link to="/fin/prepaid/reports/forecast">{t('forecastReport')}</Link>} loading={isLoading}>
            {forecastPeriods.length === 0 || Number(data?.forecast.grand_total ?? 0) === 0 ? (
              <Empty description="No open recognition" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={2}>
                {Number(data?.forecast.totals.OVERDUE ?? 0) > 0 && (
                  <Text type="danger">Overdue (earlier periods): {amount(data?.forecast.totals.OVERDUE)}</Text>
                )}
                {forecastPeriods.map((p) => (
                  <Row key={p} gutter={8} align="middle">
                    <Col span={4}>
                      <Text>{dayjs(`${p}-01`).format('MMM YYYY')}</Text>
                    </Col>
                    <Col span={14}>
                      <Progress percent={Math.round((Number(data?.forecast.totals[p] ?? 0) / forecastMax) * 100)} showInfo={false} size="small" />
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}>
                      <Text>{amount(data?.forecast.totals[p])}</Text>
                    </Col>
                  </Row>
                ))}
                {Number(data?.forecast.totals.LATER ?? 0) > 0 && <Text type="secondary">Later: {amount(data?.forecast.totals.LATER)}</Text>}
              </Space>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Card size="small" title={t('prepaidByCategory')} loading={isLoading}>
                <Table
                  size="small"
                  rowKey="category"
                  pagination={false}
                  dataSource={data?.remaining_by_category ?? []}
                  columns={[
                    { title: t('category'), dataIndex: 'category' },
                    { title: t('contracts'), dataIndex: 'contract_count', width: 90, align: 'right' },
                    { title: t('remaining'), dataIndex: 'remaining_base', width: 140, align: 'right', render: (v: string) => amount(v) },
                    { title: t('share'), key: 'share', width: 120, render: (_: unknown, r) => shareBar(r.remaining_base) },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={t('prepaidBySupplierTop10')} loading={isLoading}>
            <Table
              size="small"
              rowKey="supplier"
              pagination={false}
              dataSource={data?.remaining_by_supplier ?? []}
              columns={[
                { title: t('supplier'), dataIndex: 'supplier', ellipsis: true },
                { title: t('contracts'), dataIndex: 'contract_count', width: 80, align: 'right' },
                { title: t('remaining'), dataIndex: 'remaining_base', width: 130, align: 'right', render: (v: string) => amount(v) },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={t('expiryAgeingRemaining')} loading={isLoading}>
            <Table
              size="small"
              rowKey="bucket"
              pagination={false}
              dataSource={agingRows}
              columns={[
                { title: t('recognitionEndsIn'), dataIndex: 'bucket' },
                { title: t('remaining'), dataIndex: 'value', align: 'right', render: (v: string) => amount(v) },
                { title: t('share'), key: 'share', width: 110, render: (_: unknown, r) => shareBar(r.value) },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title={t('statusDistribution')} loading={isLoading}>
            <Table
              size="small"
              rowKey="status"
              pagination={false}
              dataSource={data?.status_distribution ?? []}
              columns={[
                { title: tc('status'), dataIndex: 'status', render: (s: string) => <Link to={`/fin/prepaid/contracts?status=${s}`}><StatusTag status={s} /></Link> },
                { title: t('contracts'), dataIndex: 'contract_count', width: 90, align: 'right' },
                { title: t('remaining'), dataIndex: 'remaining_base', width: 130, align: 'right', render: (v: string) => amount(v) },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs
          activeKey={tab}
          onChange={(key) => setParams({ tab: key })}
          items={[
            {
              key: 'due',
              label: t('dueForRecognitionCount', { count: data?.due_lines.length ?? 0 }),
              children: (
                <>
                  {access.canPost && (
                    <Space style={{ marginBottom: 8 }}>
                      <Button type="primary" disabled={!selectedDue.length} loading={postLines.isPending} onClick={() => postLines.mutate(selectedDue)}>
                        {t('recogniseSelectedCount', { count: selectedDue.length })}
                      </Button>
                      {access.canRun && <Button onClick={() => navigate('/fin/prepaid/runs/new')}>{t('newRecognitionRun')}</Button>}
                    </Space>
                  )}
                  <Table
                    size="small"
                    rowKey="id"
                    loading={isLoading}
                    dataSource={data?.due_lines ?? []}
                    pagination={{ pageSize: 20 }}
                    rowSelection={access.canPost ? { selectedRowKeys: selectedDue, onChange: (keys) => setSelectedDue(keys as string[]) } : undefined}
                    columns={[
                      { title: t('contract'), dataIndex: 'recognition_number', render: (v: string, r) => <Link to={contractPath(r.recognition_contract_id)}>{v}</Link> },
                      { title: t('description'), dataIndex: 'description', ellipsis: true },
                      { title: t('supplier'), dataIndex: 'supplier_name', ellipsis: true },
                      { title: t('period'), dataIndex: 'accounting_period_key', width: 90 },
                      {
                        title: t('postingDate'),
                        dataIndex: 'posting_date',
                        width: 120,
                        render: (v: string) => (v < dayjs(asOf).startOf('month').format('YYYY-MM-DD') ? <Text type="danger">{v}</Text> : v),
                      },
                      { title: t('amountBase'), dataIndex: 'planned_base_amount', align: 'right', width: 140, render: (v: string) => amount(v) },
                      { title: tc('status'), dataIndex: 'status', width: 110, render: (s: string) => <StatusTag status={s} /> },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'blocked',
              label: t('blockedCount', { count: data?.blocked_lines.length ?? 0 }),
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  loading={isLoading}
                  dataSource={data?.blocked_lines ?? []}
                  pagination={{ pageSize: 20 }}
                  columns={[
                    { title: t('contract'), dataIndex: 'recognition_number', render: (v: string, r) => <Link to={contractPath(r.recognition_contract_id)}>{v}</Link> },
                    { title: t('period'), dataIndex: 'accounting_period_key', width: 90 },
                    { title: t('postingDate'), dataIndex: 'posting_date', width: 110 },
                    { title: t('amountBase'), dataIndex: 'planned_base_amount', align: 'right', width: 130, render: (v: string) => amount(v) },
                    {
                      title: t('reason'),
                      key: 'reason',
                      render: (_: unknown, r) => (
                        <Space direction="vertical" size={0}>
                          <Tag color="red">{humanize(r.exception_code)}</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {r.exception_message}
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      title: tc('actions'),
                      key: 'actions',
                      width: 300,
                      render: (_: unknown, r) =>
                        access.canPost ? (
                          <Space wrap size={4}>
                            {BLOCKED_LINE_ACTIONS.map((a) => (
                              <Button key={a} size="small" onClick={() => setBlockedAction({ line: r, action: a })}>
                                {humanize(a)}
                              </Button>
                            ))}
                          </Space>
                        ) : null,
                    },
                  ]}
                />
              ),
            },
            {
              key: 'drafts',
              label: t('draftJournalsCount', { count: data?.draft_postings.length ?? 0 }),
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  loading={isLoading}
                  dataSource={data?.draft_postings ?? []}
                  pagination={{ pageSize: 20 }}
                  columns={[
                    {
                      title: t('draftJv'),
                      dataIndex: 'draft_journal_voucher_number',
                      render: (v: string, r) => (r.draft_journal_voucher_id ? <Link to={jvPath(r.draft_journal_voucher_id)}>{v}</Link> : v),
                    },
                    { title: t('postingDate'), dataIndex: 'posting_date', width: 120 },
                    { title: t('lines'), dataIndex: 'line_count', width: 80, align: 'right' },
                    { title: t('granularity'), dataIndex: 'granularity', render: (g: string) => humanize(g) },
                    { title: t('amountBase'), dataIndex: 'total_base_amount', align: 'right', width: 140, render: (v: string) => amount(v) },
                    {
                      title: tc('actions'),
                      key: 'actions',
                      width: 200,
                      render: (_: unknown, r) =>
                        access.canPost ? (
                          <Space>
                            <Button size="small" type="primary" loading={postDraft.isPending && postDraft.variables === r.id} onClick={() => postDraft.mutate(r.id)}>
                              {tc('post')}
                            </Button>
                            <Button size="small" danger onClick={() => setDiscardTarget(r.id)}>
                              {t('discard')}
                            </Button>
                          </Space>
                        ) : null,
                    },
                  ]}
                />
              ),
            },
            {
              key: 'approvals',
              label: t('pendingApprovalsCount', { count: data?.pending_approvals.length ?? 0 }),
              children: (
                <Table
                  size="small"
                  rowKey={(r) => `${r.kind}-${r.id}`}
                  loading={isLoading}
                  dataSource={data?.pending_approvals ?? []}
                  pagination={{ pageSize: 20 }}
                  columns={[
                    { title: t('type'), dataIndex: 'kind', width: 110, render: (v: string) => <Tag>{v === 'contract' ? t('newPrepaid') : t('adjustment')}</Tag> },
                    {
                      title: t('reference'),
                      dataIndex: 'number',
                      render: (v: string, r) => (
                        <Link to={r.kind === 'contract' ? contractPath(r.contract_id) : `${contractPath(r.contract_id)}?tab=adjustments`}>{v}</Link>
                      ),
                    },
                    { title: t('descriptionReason'), dataIndex: 'label', ellipsis: true },
                    { title: t('amount'), dataIndex: 'amount', align: 'right', width: 140, render: (v: string) => amount(v) },
                    { title: t('requested'), dataIndex: 'requested_at', width: 170, render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
                  ]}
                />
              ),
            },
            {
              key: 'expiring',
              label: t('expiringIn30DaysCount', { count: data?.expiring_contracts.length ?? 0 }),
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  loading={isLoading}
                  dataSource={data?.expiring_contracts ?? []}
                  pagination={{ pageSize: 20 }}
                  columns={[
                    { title: t('contract'), dataIndex: 'recognition_number', render: (v: string, r) => <Link to={contractPath(r.id)}>{v}</Link> },
                    { title: t('description'), dataIndex: 'description', ellipsis: true },
                    { title: t('recognitionEnds'), dataIndex: 'recognition_end_date', width: 140 },
                    { title: t('remainingBase'), dataIndex: 'remaining_base_amount', align: 'right', width: 150, render: (v: string) => amount(v) },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={!!blockedAction}
        title={blockedAction ? `${humanize(blockedAction.action)} — ${blockedAction.line.recognition_number} ${blockedAction.line.accounting_period_key}` : ''}
        okText={t('confirmAction')}
        confirmLoading={blockedMutation.isPending}
        okButtonProps={{ disabled: actionReason.trim().length < 3 }}
        onCancel={() => {
          setBlockedAction(null);
          setActionReason('');
        }}
        onOk={() =>
          blockedAction && blockedMutation.mutate({ lineId: blockedAction.line.id, action: blockedAction.action, reason: actionReason.trim() })
        }
      >
        <Text type="secondary">
          {blockedAction?.action === 'POST_TO_NEXT_OPEN_PERIOD' && t('blockedActionPostNextPeriod')}
          {blockedAction?.action === 'CURRENT_PERIOD_ADJUSTMENT' && t('blockedActionCurrentPeriodAdj')}
          {blockedAction?.action === 'HOLD' && t('blockedActionHold')}
          {blockedAction?.action === 'RELEASE' && t('blockedActionRelease')}
          {blockedAction?.action === 'REQUEST_REOPENING' && t('blockedActionRequestReopening')}
        </Text>
        <Input.TextArea rows={3} style={{ marginTop: 12 }} placeholder={t('reasonRequiredAudited')} value={actionReason} onChange={(e) => setActionReason(e.target.value)} />
      </Modal>

      <Modal
        open={!!discardTarget}
        title={t('discardDraftRecognitionJournal')}
        okText={t('discard')}
        okButtonProps={{ danger: true, disabled: actionReason.trim().length < 3 }}
        confirmLoading={discardDraft.isPending}
        onCancel={() => {
          setDiscardTarget(null);
          setActionReason('');
        }}
        onOk={() => discardTarget && discardDraft.mutate({ id: discardTarget, reason: actionReason.trim() })}
      >
        <Text type="secondary">{t('draftJvCancelHint')}</Text>
        <Input.TextArea rows={3} style={{ marginTop: 12 }} placeholder={t('reasonRequired')} value={actionReason} onChange={(e) => setActionReason(e.target.value)} />
      </Modal>
    </div>
  );
};

export default PrepaidWorkspacePage;
